import { __generator, __read, __values } from "tslib";
import { serializer, mapFilterNullish } from '../../common';
import { getFills, getFrame, getMatrix, getStrokes, getChildIds, } from '../../item/item';
import { isParent } from '../../item/shared';
import { ItemClass } from '../../item/shared/types';
import { MatrixUtils, RectangleUtils, PointUtils } from '../../math';
import { getItemById, getRoot, isNotRoot } from '../fragment';
import { CanvasUtils, ItemUtils } from '../../item';
import { TextSizing } from '../../item/types';
export function getEntities(fragment) {
    return {
        items: getItems(fragment),
    };
}
export function getItems(fragment) {
    return fragment.items;
}
/**
 *
 * @param fragment
 * @param id the id of the child item
 * @returns the parent of the item matching the provided id.
 * If the item cannot be found, null is returned.
 * If the item is found and its parent is the fragment root, fragment root is returned.
 */
export function getParent(fragment, id) {
    var item = getItemById(fragment, id);
    if (item === null)
        return null;
    return (item.parentId
        ? getItemById(fragment, item.parentId)
        : fragment.root);
}
export function getChildren(fragment, id) {
    var item = id ? getItemById(fragment, id) : getRoot(fragment);
    if (item === null || !isParent(item))
        return [];
    return mapFilterNullish(item.children, function (childId) {
        return getItemById(fragment, childId);
    });
}
export function getAncestors(fragment, id) {
    var ancestors = [];
    var parent = getParent(fragment, id);
    while (parent !== null && isNotRoot(fragment, parent)) {
        ancestors.push(parent);
        parent = getParent(fragment, parent.id);
    }
    // Check if we exited the loop when we found the root
    // If the root is not the canvas (for example, a Group), add it as ancestor
    if (parent && !CanvasUtils.isCanvas(parent)) {
        ancestors.push(parent);
    }
    return ancestors;
}
/**
 * @returns the deepest nested items (leaf nodes)
 */
export function getExternalItems(fragment) {
    var e_1, _a;
    var res = [];
    try {
        // eslint-disable-next-line no-restricted-syntax
        for (var _b = __values(walk(fragment)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 1), item = _d[0];
            var hasChildren = isParent(item) && !ItemUtils.getChildIds(item).length;
            if (!isParent(item) || hasChildren) {
                res.push(item);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return res;
}
export function walk(fragment, id, ctx) {
    var children, children_1, children_1_1, child, childIds, e_2_1;
    var e_2, _a;
    if (ctx === void 0) { ctx = {
        depth: 0,
    }; }
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                children = getChildren(fragment, id);
                _b.label = 1;
            case 1:
                _b.trys.push([1, 7, 8, 9]);
                children_1 = __values(children), children_1_1 = children_1.next();
                _b.label = 2;
            case 2:
                if (!!children_1_1.done) return [3 /*break*/, 6];
                child = children_1_1.value;
                return [4 /*yield*/, [child, ctx]];
            case 3:
                _b.sent();
                childIds = getChildIds(child);
                if (!childIds) return [3 /*break*/, 5];
                return [5 /*yield**/, __values(walk(fragment, child.id, {
                        depth: ctx.depth + 1,
                    }))];
            case 4:
                _b.sent();
                _b.label = 5;
            case 5:
                children_1_1 = children_1.next();
                return [3 /*break*/, 2];
            case 6: return [3 /*break*/, 9];
            case 7:
                e_2_1 = _b.sent();
                e_2 = { error: e_2_1 };
                return [3 /*break*/, 9];
            case 8:
                try {
                    if (children_1_1 && !children_1_1.done && (_a = children_1.return)) _a.call(children_1);
                }
                finally { if (e_2) throw e_2.error; }
                return [7 /*endfinally*/];
            case 9: return [2 /*return*/];
        }
    });
}
export function getAncestorIds(fragment, id) {
    return getAncestors(fragment, id).map(function (item) { return item.id; });
}
/**
 * @returns the ids of the deepest nested items (leaf nodes)
 */
export function getExternalItemIds(fragment) {
    return getExternalItems(fragment).map(function (item) { return item.id; });
}
export function getAncestorMatrix(fragment, id) {
    var ancestors = getAncestors(fragment, id);
    var ancestorMatrices = ancestors.map(function (item) { return item.itemProps.matrix; });
    return ancestorMatrices.reduce(function (acc, matrix) { return MatrixUtils.multiply(matrix, acc); }, MatrixUtils.create());
}
export function getSiblings(fragment, id) {
    return mapFilterNullish(getSiblingIds(fragment, id), function (siblingId) {
        return getItemById(fragment, siblingId);
    });
}
export function getSiblingIds(fragment, id) {
    var _a;
    var parent = getParent(fragment, id);
    return (_a = parent === null || parent === void 0 ? void 0 : parent.children) !== null && _a !== void 0 ? _a : [];
}
export function getItemFills(fragment, id) {
    var item = getItemById(fragment, id);
    return item ? getFills(item) : [];
}
export function getItemPosition(fragment, id) {
    var bounds = getBounds(fragment, id);
    return bounds ? bounds.point : null;
}
export function getItemStrokes(fragment, id) {
    var item = getItemById(fragment, id);
    return item ? getStrokes(item) : [];
}
// accept id or nodes so that caller doesn't need to check which they have
// as this is often passed children lists
export function getItemsById(fragment, idsOrNodes) {
    var items = getItems(fragment);
    return idsOrNodes.map(function (idOrNode) {
        return typeof idOrNode === 'string' ? items[idOrNode] : idOrNode;
    });
}
export function getDenormalizedItemById(fragment, id) {
    var normalizedItem = getItemById(fragment, id);
    return serializer.denormalizeItem(normalizedItem, getEntities(fragment));
}
// In some edge cases, getBounds may return null. For example, a Group item
// without any children.
export function getBounds(fragment, id, global, noCache // ignore this item's frame if its present and recalculate, still uses the frames of children
) {
    if (global === void 0) { global = false; }
    if (noCache === void 0) { noCache = false; }
    var item = getItemById(fragment, id);
    if (item === null)
        return null;
    var bounds = null;
    if (!noCache) {
        bounds = getFrame(item);
    }
    if (bounds === null) {
        // have to use a switch here instead of a dictionary of class to function as
        // TS has no correlated record type support: https://github.com/Microsoft/TypeScript/issues/30581
        switch (item.className) {
            case ItemClass.CompoundPath:
            case ItemClass.Group:
                bounds = getGroupBounds(fragment, item);
                break;
            case ItemClass.Mask:
                bounds = getMaskBounds(fragment, item);
                break;
            case ItemClass.Path:
                bounds = getPathBounds(fragment, item);
                break;
            case ItemClass.Raster:
            case ItemClass.Shape:
                bounds = getSizedBounds(fragment, item);
                break;
            case ItemClass.Text:
                bounds = getTextBounds(fragment, item);
                break;
            default:
                break;
        }
    }
    return bounds !== null && global
        ? RectangleUtils.transform(bounds, getAncestorMatrix(fragment, id))
        : bounds;
}
export function getUnitedBounds(fragment, itemIds, global) {
    if (global === void 0) { global = false; }
    var boundsList = mapFilterNullish(itemIds, function (id) {
        return getBounds(fragment, id, global);
    });
    if (boundsList.length === 0)
        return null;
    return RectangleUtils.uniteAll(boundsList);
}
function getGroupBounds(fragment, item) {
    if (item.children.length === 0)
        return null;
    var matrix = getMatrix(item);
    var childrenBounds = getUnitedBounds(fragment, item.children);
    if (childrenBounds === null) {
        // group of empty groups
        return null;
    }
    return RectangleUtils.transform(childrenBounds, matrix);
}
function getMaskBounds(fragment, mask) {
    // TODO: work out the intersection between the mask and the masked items
    return getBounds(fragment, mask.children[0]);
}
function getPathBounds(_fragment, item) {
    if (item.segments.length === 0)
        return null;
    // TODO: take curves into account
    var segments = item.segments;
    var _a = segments.reduce(function (acc, _a) {
        var _b = __read(_a, 2), x = _b[0], y = _b[1];
        return ({
            min: PointUtils.min(acc.min, [x, y]),
            max: PointUtils.max(acc.max, [x, y]),
        });
    }, {
        min: [Infinity, Infinity],
        max: [-Infinity, -Infinity],
    }), min = _a.min, max = _a.max;
    return RectangleUtils.create(min, PointUtils.subtract(max, min));
}
function getTextBounds(_fragment, item) {
    var size = item.size, sizing = item.sizing;
    var matrix = getMatrix(item);
    var rectangle = RectangleUtils.create([0, 0], size);
    var transformedSize = RectangleUtils.transform(rectangle, matrix);
    if (sizing[0] === TextSizing.Auto) {
        // eslint-disable-next-line prefer-destructuring
        transformedSize.size[0] = size[0];
    }
    if (sizing[1] === TextSizing.Auto) {
        // eslint-disable-next-line prefer-destructuring
        transformedSize.size[1] = size[1];
    }
    return transformedSize;
}
function getSizedBounds(_fragment, item) {
    var size = item.size;
    var matrix = getMatrix(item);
    var rectangle = RectangleUtils.create([0, 0], size);
    return RectangleUtils.transform(rectangle, matrix);
}
